import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { 
  TextField, 
  Dialog, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box,
  IconButton,
  Paper,
  Fade
} from "@mui/material";
import withStyles from '@mui/styles/withStyles';
import ButtonCircularProgress from "../../../shared/components/ButtonCircularProgress";
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import axios from "axios";

const styles = (theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '24px',
      padding: 0,
      maxWidth: '480px',
      width: '100%',
      margin: theme.spacing(2),
      background: theme.palette.mode === 'dark' 
        ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
        : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 25px 50px -12px rgba(0, 0, 0, 0.8)'
        : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(3, 3, 2, 3),
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#333' : '#e2e8f0'}`,
  },
  closeButton: {
    color: theme.palette.common.text,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f1f5f9',
    },
  },
  content: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  emailIcon: {
    fontSize: '48px',
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9',
    borderRadius: '50%',
    padding: theme.spacing(2),
  },
  successIcon: {
    fontSize: '64px',
    color: '#10b981',
  },
  textField: {
    marginTop: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: '16px',
      backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#f8fafc',
      '& fieldset': {
        borderColor: theme.palette.mode === 'dark' ? '#334155' : '#e2e8f0',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.common.text,
    },
    '& .MuiOutlinedInput-input': {
      color: theme.palette.common.text,
      padding: theme.spacing(1.5, 2),
    },
  },
  actions: {
    padding: theme.spacing(2, 3, 3, 3),
    gap: theme.spacing(2),
    justifyContent: 'center',
  },
  resetButton: {
    borderRadius: '16px',
    padding: theme.spacing(1.5, 4),
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '16px',
    minWidth: '140px',
  },
  cancelButton: {
    borderRadius: '16px',
    padding: theme.spacing(1.5, 4),
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '16px',
    color: theme.palette.common.text,
    borderColor: theme.palette.mode === 'dark' ? '#334155' : '#e2e8f0',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#f8fafc',
    },
  },
  successContainer: {
    padding: theme.spacing(4, 3),
    textAlign: 'center',
  },
  successButton: {
    borderRadius: '16px',
    padding: theme.spacing(1.5, 4),
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '16px',
    marginTop: theme.spacing(3),
    minWidth: '140px',
  },
});

function ChangePassword(props) {
  const { onClose, classes, setLoginStatus, theme } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [email, setEmail] = useState("");

  const sendPasswordEmail = useCallback((event) => {
    event.preventDefault();
    setIsLoading(true);
    console.log('sending password reset email');
    let data = { email: email };
    axios.post("/api/users/send-password-reset", data).then((res) => {
      console.log('response from password reset api..');
      console.log(res);
      setIsLoading(false);
      setIsSent(true);
    }).catch((err) => {
      console.log('Error sending password reset:', err);
      setIsLoading(false);
    });
  }, [email]);

  return (
    <Dialog 
      open
      onClose={onClose}
      disableEscapeKeyDown={isLoading}
      className={classes.dialog}
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      {isSent ? (
        <Box className={classes.successContainer}>
          <div className={classes.iconContainer}>
            <CheckCircleIcon className={classes.successIcon} />
          </div>
          <Typography 
            variant="h5" 
            fontWeight="bold" 
            sx={{ 
              color: theme.palette.common.text,
              marginBottom: theme.spacing(2)
            }}
          >
            Check Your Email
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: theme.palette.common.text,
              opacity: 0.8,
              lineHeight: 1.6,
              marginBottom: theme.spacing(1)
            }}
          >
            We've sent password reset instructions to
          </Typography>
          <Typography 
            variant="body1" 
            fontWeight="600"
            sx={{ 
              color: theme.palette.primary.main,
              marginBottom: theme.spacing(2)
            }}
          >
            {email}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.common.text,
              opacity: 0.7,
              fontSize: '14px'
            }}
          >
            Please check your inbox and follow the instructions to reset your password.
          </Typography>
          <Button
            variant="contained"
            onClick={onClose}
            className={classes.successButton}
          >
            Got it
          </Button>
        </Box>
      ) : (
        <>
          <div className={classes.header}>
            <Typography 
              variant="h6" 
              fontWeight="bold"
              sx={{ color: theme.palette.common.text }}
            >
              Reset Password
            </Typography>
            <IconButton 
              onClick={onClose} 
              disabled={isLoading}
              className={classes.closeButton}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </div>
          
          <form onSubmit={sendPasswordEmail}>
            <div className={classes.content}>
              <div className={classes.iconContainer}>
                <EmailIcon className={classes.emailIcon} />
              </div>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: theme.palette.common.text,
                  marginBottom: theme.spacing(1),
                  lineHeight: 1.6
                }}
              >
                Enter your email address and we'll send you instructions to reset your password.
              </Typography>
              
              <TextField
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={classes.textField}
                variant="outlined"
                margin="dense"
                required
                fullWidth
                label="Email Address"
                autoFocus
                type="email"
                autoComplete="off"
                disabled={isLoading}
              />
            </div>
            
            <div className={classes.actions}>
              <Button 
                onClick={onClose} 
                disabled={isLoading}
                variant="outlined"
                className={classes.cancelButton}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading || !email.trim()}
                className={classes.resetButton}
              >
                {isLoading ? (
                  <>
                    Sending...
                    <ButtonCircularProgress size={20} style={{ marginLeft: 8 }} />
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </Button>
            </div>
          </form>
        </>
      )}
    </Dialog>
  );
}

ChangePassword.propTypes = {
  onClose: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  setLoginStatus: PropTypes.func.isRequired,
};

export default withStyles(styles, { withTheme: true })(ChangePassword);
